import React, { useEffect, useRef, useState } from 'react';
import { getTailwindColourShades } from './ColorPicker';
import {
  clearMembershipFormValidation,
  validateMembershipForm
} from '../utils/membershipFormValidation';
import {
  collectMembershipFormData,
  populateMembershipForm,
  setMembershipFormReadOnly
} from '../utils/membershipFormData';

const ClubTermsModal = ({
  isOpen,
  onClose,
  htmlContent = '',
  onAccept,
  acceptLabel = 'Accept',
  acceptMobileLabel = '',
  showAccept = false,
  validateOnAccept = true,
  softValidate = false,
  initialFormData = null,
  readOnly = false,
  acceptDisabled = false,
  errorMessage = '',
  footerContent = null,
  prependHtml = '',
  leftFooterActions = [],
  membershipTypeName = '',
  membershipColour = 'slate',
}) => {
  const primaryActionClass = 'rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50';
  const destructiveActionClass = 'rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50';
  const approveActionClass = 'rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50';
  const rejectActionClass = 'rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50';
  const secondaryActionClass = 'px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const iframeRef = useRef(null);
  const isWritingIframeRef = useRef(false);
  const [validationError, setValidationError] = useState('');

  const insertPrependedContent = (doc) => {
    doc.querySelectorAll('[data-membership-form-prepend]').forEach((element) => element.remove());

    const membershipTypeLabel = String(membershipTypeName || '').trim();

    const insertBanner = (html, marker) => {
      const banner = doc.createElement('div');
      banner.setAttribute('data-membership-form-prepend', marker);
      banner.innerHTML = html;
      doc.body.insertBefore(banner, doc.body.firstChild);
    };

    if (membershipTypeLabel) {
      const { bar: barColour, display } = getTailwindColourShades(membershipColour);
      const formFont = 'Arial,Helvetica,sans-serif';
      const banner = doc.createElement('div');
      banner.setAttribute('data-membership-form-prepend', 'membership-type');
      banner.style.cssText = `text-align:center;padding:8px 24px 16px;border-bottom:1px solid #f1f5f9;background:#ffffff;font-family:${formFont};color:#334155;margin:0;`;

      const bar = doc.createElement('div');
      bar.style.cssText = `width:56px;height:4px;margin:8px auto 0;background:${barColour};border-radius:9999px;`;

      const label = doc.createElement('p');
      label.style.cssText = `margin:0;font-family:${formFont};font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.14em;color:#94a3b8;`;
      label.textContent = 'Membership type';

      const name = doc.createElement('p');
      name.style.cssText = `margin:12px 0 0;font-family:${formFont};font-size:20px;font-weight:600;letter-spacing:-0.025em;color:${display};`;
      name.textContent = membershipTypeLabel;

      banner.appendChild(label);
      banner.appendChild(bar);
      banner.appendChild(name);
      doc.body.insertBefore(banner, doc.body.firstChild);
    }

    if (prependHtml) {
      insertBanner(prependHtml, 'prepend-html');
    }
  };

  const writeIframeContent = () => {
    if (!iframeRef.current || isWritingIframeRef.current) return;

    isWritingIframeRef.current = true;

    try {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (!doc) return;

      doc.open();
      doc.write(htmlContent || '');
      doc.close();

      if (initialFormData && Object.keys(initialFormData).length > 0) {
        populateMembershipForm(doc, initialFormData);
      }

      if (readOnly) {
        setMembershipFormReadOnly(doc, true);
      }

      insertPrependedContent(doc);
    } catch (error) {
      console.error('Error updating iframe:', error);
    } finally {
      isWritingIframeRef.current = false;
    }
  };

  useEffect(() => {
    if (!isOpen || !iframeRef.current) return undefined;

    setValidationError('');
    writeIframeContent();
    const timeoutId = setTimeout(writeIframeContent, 100);

    return () => clearTimeout(timeoutId);
  }, [htmlContent, isOpen, initialFormData, readOnly, prependHtml, membershipTypeName, membershipColour]);

  const handleAccept = () => {
    if (!onAccept) return;

    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;

    if (validateOnAccept && showAccept) {
      const result = validateMembershipForm(doc);

      if (softValidate) {
        if (result.valid) {
          clearMembershipFormValidation(doc);
        }
        setValidationError('');
        onAccept(collectMembershipFormData(doc), { valid: result.valid });
        return;
      }

      if (!result.valid) {
        setValidationError('Please complete all required fields before continuing.');
        result.firstInvalidField?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        result.firstInvalidField?.focus?.();
        return;
      }

      clearMembershipFormValidation(doc);
    }

    setValidationError('');
    const formData = showAccept ? collectMembershipFormData(doc) : {};
    onAccept(formData);
  };

  const handleClose = () => {
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    clearMembershipFormValidation(doc);
    setValidationError('');
    onClose();
  };

  const collectValidatedFormData = () => {
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    const result = validateMembershipForm(doc);
    return {
      formData: collectMembershipFormData(doc),
      valid: result.valid,
      firstInvalidField: result.firstInvalidField
    };
  };

  const handleLeftFooterAction = (action) => {
    if (action.requiresFormData) {
      const { formData, valid, firstInvalidField } = collectValidatedFormData();

      if (!softValidate && !valid) {
        setValidationError('Please complete all required fields before continuing.');
        firstInvalidField?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        firstInvalidField?.focus?.();
        return;
      }

      if (valid) {
        clearMembershipFormValidation(
          iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document
        );
      }

      setValidationError('');
      action.onClick(formData, { valid });
      return;
    }

    action.onClick();
  };

  const getLeftActionClassName = (variant) => {
    if (variant === 'approve') return approveActionClass;
    if (variant === 'reject') return rejectActionClass;
    if (variant === 'destructive') return destructiveActionClass;
    if (variant === 'secondary') return secondaryActionClass;
    return primaryActionClass;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl overflow-hidden max-h-[90vh] flex flex-col relative">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 text-slate-600 hover:text-slate-600 focus:outline-none bg-white rounded-full p-1 shadow-sm"
          aria-label="Close club terms"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: '70vh' }}>
          <iframe
            ref={iframeRef}
            title="Club Terms"
            className="w-full h-full border-0"
            style={{ minHeight: '70vh' }}
          />
        </div>
        <div className="shrink-0 px-6 py-4 border-t border-slate-200 flex flex-col gap-3">
          {validationError || errorMessage ? (
            <p className="text-sm text-red-600">{validationError || errorMessage}</p>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {leftFooterActions.filter((action) => !action.hidden).map((action) => (
                <button
                  key={action.key || action.label}
                  type="button"
                  onClick={() => handleLeftFooterAction(action)}
                  disabled={action.disabled}
                  className={getLeftActionClassName(action.variant)}
                >
                  {action.mobileLabel ? (
                    <>
                      <span className="sm:hidden">{action.mobileLabel}</span>
                      <span className="hidden sm:inline">{action.label}</span>
                    </>
                  ) : action.label}
                </button>
              ))}
              {footerContent}
            </div>
            <div className="flex items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className={secondaryActionClass}
              >
                Close
              </button>
              {showAccept && (
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={acceptDisabled}
                  className={primaryActionClass}
                >
                  {acceptMobileLabel ? (
                    <>
                      <span className="sm:hidden">{acceptMobileLabel}</span>
                      <span className="hidden sm:inline">{acceptLabel}</span>
                    </>
                  ) : acceptLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubTermsModal;
